/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';

import roomPlaceholder from '@/img/hotel_page/room_placeholder.png';

import {
    IconBack,
    IconBed,
    IconFreeCancellation,
    IconInPolicy,
    IconMeal,
    IconNonRefundable,
    IconOutOfPolicy,
    IconPhoto,
    IconRoomOnly,
    IconSize,
    IconSleeps,
    IconView,
    PerkIcon,
} from './DealViewIcons.react';
import {
    LABEL_AVG_ROOM_NIGHT,
    LABEL_BEFORE,
    LABEL_FREE_CANCELLATION,
    LABEL_IN_POLICY,
    LABEL_NON_REFUNDABLE,
    LABEL_NOT_DEFINED,
    LABEL_OUT_OF_POLICY,
    POLICY_IN,
    POLICY_OUT,
    boardLabelOf,
    cancellationOf,
    isRoomOnly,
    supplierCodeOf,
} from './dealViewModel';

import './DealViewParts.scss';

/** Render pieces both dev panels share, so the two representations draw a chip or a deal line alike. */

export const COLOR_PRIMARY = '#275180';
export const COLOR_TEXT = '#20242A';
export const COLOR_SECONDARY = '#4B575B';
export const COLOR_MUTED = '#919595';
export const COLOR_BORDER = '#DFE6EC';
export const COLOR_PANEL_BG = '#F9FAFB';
export const COLOR_GREEN = '#008F4B';
export const COLOR_AMBER = '#A85700';
// `design-tokens.css` `--color-error-er400-main`.
export const COLOR_ERROR = '#ff5353';
export const COLOR_RADIO_ACCENT = '#356E82';

export const LABEL_MORE_DETAILS = 'More details';
export const LABEL_BOOK = 'Book';
const LABEL_WITHOUT_CARD_ONE = 'deal without a card';
const LABEL_WITHOUT_CARD_MANY = 'deals without a card';
const TITLE_NOT_DEFINED = 'Not present in the room details';
const TITLE_BED_SOURCE_PREFIX = 'Bed source: ';

/**
 * Both dev panels are the same fixed width (summary row included) so they align with each other;
 * narrower viewports overflow horizontally rather than shrink the panel. `DealViewParts.scss` overrides
 * the width under the ≤700px mobile rules, where the panel becomes a static 500px capped to the viewport.
 *
 * The panel is wider than `.hotel-main` (816px) and overflows under the later-sibling `.hotel-side`
 * (266px), which stretches to the container's full height and, as the later non-positioned box,
 * paints above the overflow and intercepts clicks on the right-hand part of the panel (the table's
 * Book button). Positioning the panel with a z-index lifts it over that region.
 */
export const PANEL_WIDTH_PX = 1082;
const PANEL_Z_INDEX = 1;
const PANEL_CLASS = 'dvp-panel';

// Same visual style as the `RoomGroups` summary row (`RoomGroups.react.js` `styles.component` / `styles.summary`).
const panelStyles = {
    component: {
        width: `${PANEL_WIDTH_PX}px`,
        position: 'relative',
        zIndex: PANEL_Z_INDEX,
        background: '#fff',
        boxSizing: 'border-box',
        border: '1px solid lightgrey',
        borderRadius: '4px',
        padding: '4px 4px 4px 15px',
        marginTop: '10px',
    },
    summary: { cursor: 'pointer', userSelect: 'none', fontWeight: 600, padding: '4px 0' },
    body: { padding: '6px 6px 10px 0' },
};

export const CollapsiblePanel = ({ title, count, children }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className={PANEL_CLASS} style={panelStyles.component}>
            <div style={panelStyles.summary} onClick={() => setOpen(!open)}>
                {open ? '▾' : '▸'} {title} ({count})
            </div>
            {open && <div style={panelStyles.body}>{children}</div>}
        </div>
    );
};

export const sheetStyle = {
    borderRadius: '16px',
    background: COLOR_PANEL_BG,
    boxShadow: `inset 0 0 0 1px ${COLOR_BORDER}`,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
};

export const buttonPrimaryStyle = {
    border: 0,
    borderRadius: '8px',
    background: COLOR_PRIMARY,
    color: '#fff',
    fontFamily: 'inherit',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
};

const chipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    lineHeight: '16px',
    color: COLOR_TEXT,
};

const chipNotDefinedStyle = { ...chipStyle, color: COLOR_ERROR };

const linkStyle = { fontSize: '13px', lineHeight: '16px', color: COLOR_PRIMARY, cursor: 'pointer' };
const linkDisabledStyle = { ...linkStyle, color: COLOR_MUTED, cursor: 'default', textDecoration: 'none' };

export const PhotoBadge = ({ count, right = 8, bottom = 8 }) => {
    if (!count) {
        return null;
    }
    return (
        <div
            style={{
                position: 'absolute',
                right: `${right}px`,
                bottom: `${bottom}px`,
                height: '28px',
                borderRadius: '4px',
                background: 'rgba(19,24,44,0.8)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '0 8px',
            }}
        >
            <IconPhoto />
            <span style={{ fontSize: '13px', lineHeight: '100%', color: '#fff' }}>{count}</span>
        </div>
    );
};

const LABEL_PREVIOUS_IMAGE = 'Previous image';
const LABEL_NEXT_IMAGE = 'Next image';
const IMAGE_NAV_STEP_BACK = -1;
const IMAGE_NAV_STEP_FORWARD = 1;
const IMAGE_NAV_EDGE_OFFSET_PX = 8;

const imageNavButtonStyle = {
    position: 'absolute',
    top: '60px',
    width: '40px',
    height: '40px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    border: 0,
    borderRadius: '100px',
    background: 'rgba(255,255,255,0.8)',
    boxShadow: 'inset 0 0 0 1px #D7DADA',
    cursor: 'pointer',
};
const imageNavPrevStyle = { ...imageNavButtonStyle, left: `${IMAGE_NAV_EDGE_OFFSET_PX}px` };
const imageNavNextStyle = { ...imageNavButtonStyle, right: `${IMAGE_NAV_EDGE_OFFSET_PX}px` };
const mirroredIconStyle = { display: 'flex', transform: 'scaleX(-1)' };

const ImageNavButton = ({ label, style, mirrored, onClick }) => (
    <button
        type="button"
        aria-label={label}
        style={style}
        onClick={(event) => {
            event.stopPropagation();
            onClick();
        }}
    >
        {mirrored ? (
            <span style={mirroredIconStyle}>
                <IconBack />
            </span>
        ) : (
            <IconBack />
        )}
    </button>
);

/**
 * One room picture. With `navigable`, a card that carries more than one image gets the mock's
 * previous/next arrows and steps through `details.images` with wrap-around; otherwise (the default)
 * it shows `details.image` exactly as before.
 */
export const RoomImage = ({ details, title, height, badgeRight, badgeBottom, radius = 8, navigable = false }) => {
    const images = details.images || [];
    const canNavigate = navigable && images.length > 1;
    const [index, setIndex] = useState(0);
    useEffect(() => setIndex(0), [details.images]);
    const current = canNavigate ? images[index] || images[0] : details.image;
    const [src, setSrc] = useState(current);
    useEffect(() => setSrc(current), [current]);
    // A URL that fails to load falls back to the placeholder; the placeholder itself never re-triggers the swap.
    const onError = () => {
        if (src !== roomPlaceholder) {
            setSrc(roomPlaceholder);
        }
    };
    const step = (direction) => setIndex((i) => (((i + direction) % images.length) + images.length) % images.length);
    return (
        <div
            style={{
                position: 'relative',
                height: `${height}px`,
                borderRadius: `${radius}px`,
                overflow: 'hidden',
                flexShrink: 0,
            }}
        >
            <img
                src={src}
                alt={title}
                onError={onError}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            {canNavigate && (
                <ImageNavButton
                    label={LABEL_PREVIOUS_IMAGE}
                    style={imageNavPrevStyle}
                    onClick={() => step(IMAGE_NAV_STEP_BACK)}
                />
            )}
            {canNavigate && (
                <ImageNavButton
                    label={LABEL_NEXT_IMAGE}
                    style={imageNavNextStyle}
                    mirrored
                    onClick={() => step(IMAGE_NAV_STEP_FORWARD)}
                />
            )}
            <PhotoBadge count={details.imageCount} right={badgeRight} bottom={badgeBottom} />
        </div>
    );
};

const DetailChip = ({ icon, value, title }) => {
    if (value === undefined) {
        return (
            <span style={chipNotDefinedStyle} title={TITLE_NOT_DEFINED}>
                {React.cloneElement(icon, { stroke: COLOR_ERROR })}
                {LABEL_NOT_DEFINED}
            </span>
        );
    }
    return (
        <span style={chipStyle} title={title}>
            {icon}
            {value}
        </span>
    );
};

/** Size · sleeps · bed (+ view when the card states one). An `undefined` value renders the red "not defined". */
export const DetailChips = ({ details, includeView = false }) => (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <DetailChip icon={<IconSize />} value={details.size} />
        <DetailChip icon={<IconSleeps />} value={details.sleeps} />
        <DetailChip
            icon={<IconBed />}
            value={details.bed}
            title={details.bedSource ? `${TITLE_BED_SOURCE_PREFIX}${details.bedSource}` : undefined}
        />
        {includeView && details.view !== undefined && <DetailChip icon={<IconView />} value={details.view} />}
    </div>
);

export const PerkChip = ({ name, size = 16, fontSize = '13px', lineHeight = '16px', gap = '6px' }) => (
    <span style={{ ...chipStyle, gap, fontSize, lineHeight }}>
        <PerkIcon name={name} size={size} />
        {name}
    </span>
);

export const MoreDetailsLink = ({ onClick, disabled }) => {
    if (disabled) {
        return <span style={linkDisabledStyle}>{LABEL_MORE_DETAILS}</span>;
    }
    return (
        <a
            href="#"
            style={linkStyle}
            onClick={(event) => {
                event.preventDefault();
                onClick();
            }}
        >
            {LABEL_MORE_DETAILS}
        </a>
    );
};

/** Two lines of up to three perk chips, then the "More details" link. */
export const PerkLines = ({ details, onMoreDetails, columnGap = '14px', rowGap = '6px' }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: rowGap }}>
        {details.perkLines.map((line, index) => (
            <div
                key={index}
                style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: `${rowGap} ${columnGap}` }}
            >
                {line.map((perk, perkIndex) => (
                    <PerkChip key={`${perkIndex}-${perk}`} name={perk} />
                ))}
            </div>
        ))}
        <div>
            <MoreDetailsLink onClick={onMoreDetails} disabled={!details.hasDetails} />
        </div>
    </div>
);

export const CancellationText = ({ cancellation, fontSize = '14px', lineHeight = '18px', color }) => {
    if (cancellation.nonRefundable) {
        return <span style={{ fontSize, lineHeight, color: color || COLOR_SECONDARY }}>{LABEL_NON_REFUNDABLE}</span>;
    }
    return (
        <span style={{ fontSize, lineHeight, color: color || COLOR_GREEN }}>
            <strong>{LABEL_FREE_CANCELLATION}</strong>
            {cancellation.deadline ? ` ${LABEL_BEFORE} ${cancellation.deadline}` : ''}
        </span>
    );
};

export const CancellationIcon = ({ cancellation, size = 18 }) =>
    cancellation.nonRefundable ? <IconNonRefundable size={size} /> : <IconFreeCancellation size={size} />;

export const BoardIcon = ({ deal, size = 18 }) =>
    isRoomOnly(deal) ? <IconRoomOnly size={size} /> : <IconMeal size={size} />;

export const SupplierTag = ({ deal }) => (
    <span
        style={{
            fontSize: '11px',
            lineHeight: '14px',
            color: COLOR_MUTED,
            border: `1px solid ${COLOR_BORDER}`,
            borderRadius: '4px',
            padding: '0 4px',
            marginLeft: 'auto',
            flexShrink: 0,
        }}
        title={String(deal.id)}
    >
        {supplierCodeOf(deal)}
    </span>
);

/** The two deal lines of the table design: cancellation, then board — with the supplier tag at the end. */
export const DealLines = ({ deal }) => {
    const cancellation = cancellationOf(deal);
    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CancellationIcon cancellation={cancellation} />
                <CancellationText cancellation={cancellation} />
                <SupplierTag deal={deal} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BoardIcon deal={deal} />
                <span style={{ fontSize: '14px', lineHeight: '18px', color: COLOR_TEXT }}>{boardLabelOf(deal)}</span>
            </div>
        </>
    );
};

export const PolicyMark = ({ policy }) => {
    if (policy === POLICY_IN) {
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <IconInPolicy />
                <span style={{ fontSize: '13px', lineHeight: '16px', color: COLOR_GREEN }}>{LABEL_IN_POLICY}</span>
            </span>
        );
    }
    if (policy === POLICY_OUT) {
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <IconOutOfPolicy />
                <span style={{ fontSize: '13px', lineHeight: '16px', color: COLOR_AMBER }}>{LABEL_OUT_OF_POLICY}</span>
            </span>
        );
    }
    return null;
};

export const AvgLine = ({ lines, bold = false }) => (
    <>
        <span
            style={{
                fontSize: '13px',
                lineHeight: '16px',
                fontWeight: bold ? 700 : 400,
                color: bold ? COLOR_TEXT : COLOR_SECONDARY,
            }}
        >
            {lines.avg}
        </span>
        <span style={{ fontSize: '13px', lineHeight: '16px', color: bold ? COLOR_TEXT : COLOR_SECONDARY }}>
            {LABEL_AVG_ROOM_NIGHT}
        </span>
    </>
);

const LABEL_SUPERSEDED_HIDDEN = 'superseded hidden';
const LABEL_ALTERNATIVE_KEPT = 'alternative kept';
const LABEL_ALTERNATIVES_KEPT = 'alternatives kept';
const NOTE_SEPARATOR = ' · ';

/**
 * Dev note under a card: how many of its deals the card-level dominance rule hid, and how many
 * non-comparable visible deals share a radio address with a cheaper one (kept, not hidden).
 */
export const SupersededNote = ({ count, alternatives }) => {
    const parts = [];
    if (count) {
        parts.push(`${count} ${LABEL_SUPERSEDED_HIDDEN}`);
    }
    if (alternatives) {
        parts.push(`+${alternatives} ${alternatives === 1 ? LABEL_ALTERNATIVE_KEPT : LABEL_ALTERNATIVES_KEPT}`);
    }
    if (parts.length === 0) {
        return null;
    }
    return <div style={{ fontSize: '11px', lineHeight: '14px', color: COLOR_MUTED }}>{parts.join(NOTE_SEPARATOR)}</div>;
};

export const WithoutCardNote = ({ count }) => {
    if (!count) {
        return null;
    }
    return (
        <div style={{ fontSize: '12px', color: COLOR_MUTED }}>
            {count} {count === 1 ? LABEL_WITHOUT_CARD_ONE : LABEL_WITHOUT_CARD_MANY}
        </div>
    );
};
